import { FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { message, Popover, Spin } from 'antd'
import { LeftCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { RegularButton } from 'fronton-react'
import moment, { Moment } from 'moment'
import { BiEraser } from 'react-icons/bi'
import cloneDeep from 'lodash/cloneDeep'

import { calendarsSelector, userInfoSelector } from 'redux/selectors'
import { saveCalendar } from 'redux/actions'
import {
    ActivityCluster,
    ActivityRegion,
    ActivityStore,
    FlattenedData,
    DefaultDatesState,
    IActiveMap,
    ICalendar,
    IFilteredHierarchy,
    LocationCalendars,
    LocationDates,
    LocationDatesSimple,
    OrderDay,
    PropertyAdditional,
    Region,
    UnavailableDay,
    WeekDay
} from 'pages/my-calendars/add-calendar/interfaces'
import { CalendarsRow } from 'pages/my-calendars/add-calendar/components/calendars-row'
import { getCalendar, getFilteredCalendars } from 'shared/api/calendarsApi'
import { get } from 'shared/api/api'
import { CalendarType, LocationType, OpenBtnType } from 'pages/my-calendars/enums'
import {
    createCalendarMapItem,
    deleteEmptyAdditionalFields,
    filterItemsWithDates,
    getDateValue,
    getDateWithOffset,
    getFilledAdditionalFields,
    getUniqAdditionalFields,
    getUpdatedDates,
    isCurrentDateUnavailable,
    isInfoCalendar
} from 'shared/utils'
import {
    childToParentTypeConfig,
    datesConfig,
    depthStyle,
    disableDatesConfig,
    prevDaysMap
} from 'shared/consts'
import { DAY_STRING_FORMAT } from '../constants'
import { SupplierCalendarsContext } from 'contexts/SupplierCalendarsContext'
import {
    getDisableFilteredDates,
    getFilteredApprovedDates,
    isApprovedFilteredDates
} from 'shared/utils/approvedCalendars'
import { getClearCalendarRowDescription } from '../add-calendar/utils/getClearCalendarRowDescription'
import { getFlattenArray } from '../add-calendar/utils/getFlattenArray'
import { getRegionDataArray } from '../add-calendar/utils/getRegionDataArray'
import { updateDatesRecursively } from 'shared/utils/checkDatesInterceptions'

interface IProps {
    type: CalendarType
    openBtnState: OpenBtnType
    regionData: ActivityRegion[]
    calendarData: ICalendar | null
    locationCalendars: LocationCalendars[]
    isNeedToErase: boolean
    setOpenBtnState: (state: OpenBtnType) => void
    setRegionData: (state: ActivityRegion[]) => void
    setCalendarData: (state: ICalendar) => void
    setLocationCalendars: (state: LocationCalendars[]) => void
    calendarId?: string
    fillCalendarArrayAndGetFlags?: () => { activeCalendarItems: LocationCalendars[] }
    saveCalendarRequest?: (
        activeCalendarItems: LocationCalendars[],
        isAutoSave: boolean,
        isApprove: boolean
    ) => void
}

export const Calendar: FC<IProps> = ({
    type,
    openBtnState,
    regionData,
    calendarData,
    locationCalendars,
    isNeedToErase,
    calendarId,
    setOpenBtnState,
    setRegionData,
    setCalendarData,
    setLocationCalendars,
    fillCalendarArrayAndGetFlags,
    saveCalendarRequest
}) => {
    const history = useHistory()
    const dispatch = useDispatch()
    const { isAutoSave, isSaveSuccess, types } = useSelector(calendarsSelector)
    const {
        isDisableApprovedDates,
        setIsDisableApprovedDates,
        approvedData,
        setApprovedData,
        worksheetData,
        setWorksheetData
    } = useContext(SupplierCalendarsContext)
    const [datesMap, setDatesMap] = useState<{ [key: string]: LocationDates }>({})
    const [isMayOrNewYearHoliday, setIsMayOrNewYearHoliday] = useState({
        type: '',
        status: false
    })
    const [canAutoSave, setCanAutoSave] = useState(false)
    const [allDeliveryType, setAllDeliveryType] = useState('')
    const [orderDatesAll, setOrderDatesAll] = useState<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        orderDays: any[]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        unavailableDays: any[]
        lastOrderDate: string
        firstOrderDate: string
    }>({
        orderDays: [],
        unavailableDays: [],
        lastOrderDate: '',
        firstOrderDate: ''
    })
    const userInfo = useSelector(userInfoSelector)
    const supplierId = localStorage.getItem('supplierId')

    // RFS240725_3925: CDCalendar поправить логику с пересечением дат
    useEffect(() => {
        (async () => {
            const approvedList = await getFilteredCalendars({
                supplierId: supplierId || '',
                status: 'Approved'
            })
            setApprovedData(approvedList)
        })(),
        (async () => {
            const worksheetList = await getFilteredCalendars({
                supplierId: supplierId || '',
                status: 'Worksheet'
            })
            setWorksheetData(worksheetList)
        })()
    }, [supplierId])

    useEffect(() => {
        if (isDisableApprovedDates) {
            message.warning({
                content: (
                    <>
                        Даты пересекаются с уже имеющимися календарями! <br />
                        Выберите другой интервал дат или направьте запрос на удаление имеющегося
                        календаря
                    </>
                ),
                duration: 5
            })
        }
        setIsDisableApprovedDates(false)
    }, [isDisableApprovedDates])

    useEffect(() => {
        if (calendarData && types.map((el) => el.id).includes(calendarData.calendarTypeId)) {
            const type = types.filter((el) => el.id === calendarData.calendarTypeId)[0]

            setIsMayOrNewYearHoliday({
                type: type ? type.typeName.toUpperCase() : '',
                status: true
            })
        }
    }, [types, calendarId, calendarData])

    useEffect(() => {
        if (type !== 'info' && canAutoSave) {
            const autoSave = setInterval(() => {
                message.loading({
                    content: 'Календарь сохраняется...',
                    key: 'updatable',
                    duration: 0
                })

                if (fillCalendarArrayAndGetFlags && saveCalendarRequest) {
                    const { activeCalendarItems } = fillCalendarArrayAndGetFlags()
                    const dateset: LocationDates[] = activeCalendarItems.map((item) => ({
                        id: item.id,
                        deliveryDate: item.deliveryDate,
                        orderDate: item.orderDate,
                        firstOrderDate: item.firstOrderDate,
                        lastOrderDate: item.lastOrderDate
                    }))
                    const updatedDates = getUpdatedDates(calendarData?.dates, dateset)

                    const filteredCalendar = filterItemsWithDates(activeCalendarItems)

                    updatedDates.length && saveCalendarRequest(filteredCalendar, true, false)
                    message.destroy('updatable')
                }
            }, 30000)
            return () => {
                message.destroy('updatable')
                clearInterval(autoSave)
            }
        }
    }, [fillCalendarArrayAndGetFlags, saveCalendarRequest, canAutoSave])

    useEffect(() => {
        if (isSaveSuccess && isAutoSave && type !== 'info') {
            message.success({ content: 'Календарь сохранен!', key: 'updatable' })
            dispatch(saveCalendar.cancel())
        }
    }, [isSaveSuccess, isAutoSave])

    useEffect(() => {
        isNeedToErase && setTimeout(() => setDatesMap({}))
    }, [isNeedToErase])

    const getAndSetCalendarData = async (supplierId: string | null, calendarId: string) => {
        const calendarData = await getCalendar(supplierId || 'S1000570', calendarId)
        const { dates } = calendarData
        const calendarMap = dates.reduce(
            (prev: { [key: string]: LocationDates }, curr): { [key: string]: LocationDates } =>
                createCalendarMapItem(dates, prev, curr),
            {}
        )

        setDatesMap(calendarMap)
        setCalendarData(calendarData)
    }

    useEffect(() => {
        calendarId && getAndSetCalendarData(supplierId, calendarId)
    }, [calendarId])

    useEffect(() => {
        isSaveSuccess && calendarId && getAndSetCalendarData(supplierId, calendarId)
    }, [isSaveSuccess])

    useEffect(() => {
        // Создаем контроллер для отмены запроса
        const abortController = new AbortController()

        const getFilteredHierarchy = async () => {
            // Проверка на случай, если календарь закрыли мгновенно
            if (!calendarData) return

            const activeMap: IActiveMap = { regions: {}, clusters: {} }

            setCanAutoSave(false)

            regionData.forEach((region) => {
                activeMap.regions[region.regionId] = region.isActive
                region.clusters.forEach(
                    (cluster) => (activeMap.clusters[cluster.clusterId] = cluster.isActive)
                )
            })

            try {
                const { hierarchy, ...dates }: IFilteredHierarchy = await get<IFilteredHierarchy>({
                    url: `/api/proxy/filtered-hierarchy/${
                        calendarData?.id || ''
                    }?supplierId=${supplierId}&createdAt=${calendarData?.createdAt || ''}`,
                    // Передаем сигнал отмены в HTTP-клиент
                    signal: abortController.signal
                })

                // Обновляем состояние только если запрос не был отменен
                setOrderDatesAll((state) => ({
                    ...state,
                    lastOrderDate: dates.lastOrderDateAll,
                    firstOrderDate: dates.firstOrderDateAll
                }))

                const dataWithActivity = getRegionsWithActivity(hierarchy, activeMap)

                setRegionData(dataWithActivity)
                setLocationCalendarsFlatArray(dataWithActivity)
                setCanAutoSave(calendarData.status !== 'Approved')
            } catch (error) {
                // Игнорируем ошибку отмены, остальные ошибки логируем
                if (error.name === 'AbortError') {
                    console.log('Запрос отменен из-за смены календаря')
                    return
                }
                console.error('Ошибка загрузки иерархии:', error)
            }
        }

        if (calendarData) {
            getFilteredHierarchy()
        }

        // Функция очистки: вызывается при изменении зависимостей или размонтировании
        return () => {
            abortController.abort() // Отменяем pending запрос
        }
    }, [calendarData, supplierId, userInfo])

    useEffect(() => {
        const index = locationCalendars.findIndex((item) => item.isNeedToErase)

        if (index !== -1) {
            const copyArray = cloneDeep(locationCalendars)
            copyArray[index].isNeedToErase = false
            setLocationCalendars(copyArray)
        }
    }, [locationCalendars])

    const getParent = (parentId: number, locationType: LocationType): LocationCalendars => {
        const parent = locationCalendars.filter(
            (el) => el.locationId === parentId && el.locationType === locationType
        )[0]

        if (parent.parentId && locationType === LocationType.Cluster) {
            return getParent(parent.parentId, LocationType.Region)
        }

        return parent
    }

    const getRegionsWithActivity = (regions: Region[], activeMap: IActiveMap): ActivityRegion[] => {
        const copyArray: ActivityRegion[] = []
        regions.forEach((region) => {
            const copyClusters: ActivityCluster[] = []
            region.clusters.forEach((cluster) => {
                const copyStores: ActivityStore[] = []
                cluster.stores.forEach((store) => {
                    copyStores.push({ ...store, isNeedToErase: false })
                })
                const BBXDStores = cluster.stores.filter((el) => el.deliveryType === 'BBXD')
                const isAllStoresBBXD = BBXDStores.length === cluster.stores.length
                let storeOrderDays: {
                    orderDay: WeekDay
                }[] = []
                let storedeliveryUnavailableWeekDays: {
                    weekDay: WeekDay
                }[] = []
                const orderDaysMap = cluster.stores.reduce(
                    (
                        prev: {
                            orderDays: WeekDay[]
                            unavailableWeekDays: WeekDay[]
                        },
                        curr
                    ): {
                        orderDays: WeekDay[]
                        unavailableWeekDays: WeekDay[]
                    } => {
                        const orderDays = curr.storeOrderDays?.map((el) => el.orderDay) || []
                        const unavailableWeekDays =
                            curr.storedeliveryUnavailableWeekDays?.map((el) => el.weekDay) || []
                        if (!prev.unavailableWeekDays.length && !prev.orderDays.length) {
                            return {
                                unavailableWeekDays,
                                orderDays
                            }
                        }
                        return {
                            unavailableWeekDays: prev.unavailableWeekDays.filter((v) =>
                                unavailableWeekDays.some((v2) => v == v2)
                            ),
                            orderDays: prev.orderDays.filter((v) => orderDays.some((v2) => v == v2))
                        }
                    },
                    { orderDays: [], unavailableWeekDays: [] }
                )

                storedeliveryUnavailableWeekDays = orderDaysMap.unavailableWeekDays.map((el) => ({
                    weekDay: el as WeekDay
                }))

                storeOrderDays = orderDaysMap.orderDays.map((el) => ({
                    orderDay: el as WeekDay
                }))

                copyClusters.push({
                    ...cluster,
                    isActive: activeMap.clusters[cluster.clusterId] || false,
                    isNeedToErase: false,
                    stores: copyStores,
                    storeOrderDays,
                    storedeliveryUnavailableWeekDays,
                    deliveryType: isAllStoresBBXD ? 'BBXD' : ''
                })
            })
            const BBXDClusters = copyClusters.filter((el) => el.deliveryType === 'BBXD')
            const isAllClustersBBXD = BBXDClusters.length === copyClusters.length
            let storeOrderDays: {
                orderDay: WeekDay
            }[] = []
            let storedeliveryUnavailableWeekDays: {
                weekDay: WeekDay
            }[] = []

            const orderDaysMap = copyClusters.reduce(
                (
                    prev: {
                        orderDays: WeekDay[]
                        unavailableWeekDays: WeekDay[]
                    },
                    curr
                ): {
                    orderDays: WeekDay[]
                    unavailableWeekDays: WeekDay[]
                } => {
                    const orderDays = curr.storeOrderDays?.map((el) => el.orderDay) || []
                    const unavailableWeekDays =
                        curr.storedeliveryUnavailableWeekDays?.map((el) => el.weekDay) || []
                    if (!prev.unavailableWeekDays.length && !prev.orderDays.length) {
                        return {
                            unavailableWeekDays,
                            orderDays
                        }
                    }
                    return {
                        unavailableWeekDays: prev.unavailableWeekDays.filter((v) =>
                            unavailableWeekDays.some((v2) => v == v2)
                        ),
                        orderDays: prev.orderDays.filter((v) => orderDays.some((v2) => v == v2))
                    }
                },
                { orderDays: [], unavailableWeekDays: [] }
            )

            storedeliveryUnavailableWeekDays = orderDaysMap.unavailableWeekDays.map((el) => ({
                weekDay: el as WeekDay
            }))

            storeOrderDays = orderDaysMap.orderDays.map((el) => ({
                orderDay: el as WeekDay
            }))

            copyArray.push({
                ...region,
                isActive: activeMap.regions[region.regionId] || false,
                isNeedToErase: false,
                clusters: copyClusters,
                storeOrderDays,
                storedeliveryUnavailableWeekDays,
                deliveryType: isAllClustersBBXD ? 'BBXD' : ''
            })
        })

        let storeOrderDays: {
            orderDay: WeekDay
        }[] = []
        let storedeliveryUnavailableWeekDays: {
            weekDay: WeekDay
        }[] = []

        const orderDaysMap = copyArray.reduce(
            (
                prev: {
                    orderDays: WeekDay[]
                    unavailableWeekDays: WeekDay[]
                },
                curr
            ): {
                orderDays: WeekDay[]
                unavailableWeekDays: WeekDay[]
            } => {
                const orderDays = curr.storeOrderDays?.map((el) => el.orderDay) || []
                const unavailableWeekDays =
                    curr.storedeliveryUnavailableWeekDays?.map((el) => el.weekDay) || []
                if (!prev.unavailableWeekDays.length && !prev.orderDays.length) {
                    return {
                        unavailableWeekDays,
                        orderDays
                    }
                }
                return {
                    unavailableWeekDays: prev.unavailableWeekDays.filter((v) =>
                        unavailableWeekDays.some((v2) => v == v2)
                    ),
                    orderDays: prev.orderDays.filter((v) => orderDays.some((v2) => v == v2))
                }
            },
            { orderDays: [], unavailableWeekDays: [] }
        )

        storedeliveryUnavailableWeekDays = orderDaysMap.unavailableWeekDays.map((el) => ({
            weekDay: el as WeekDay
        }))

        storeOrderDays = orderDaysMap.orderDays.map((el) => ({
            orderDay: el as WeekDay
        }))

        const BBXDRegions = copyArray.filter((el) => el.deliveryType === 'BBXD')
        const isAllRegionsBBXD = BBXDRegions.length === copyArray.length

        setAllDeliveryType(isAllRegionsBBXD ? 'BBXD' : '')

        setOrderDatesAll((state) => ({
            ...state,
            orderDays: storeOrderDays,
            unavailableDays: storedeliveryUnavailableWeekDays
        }))

        return copyArray
    }

    const nullDateIfNotSame = (
        currentData: LocationDatesSimple | undefined,
        firstInstanceData: LocationDatesSimple
    ) => {
        if (
            !currentData ||
            currentData.firstOrderDate?.getTime() !== firstInstanceData.firstOrderDate?.getTime()
        ) {
            firstInstanceData.firstOrderDate = null
        }
        if (
            !currentData ||
            currentData.lastOrderDate?.getTime() !== firstInstanceData.lastOrderDate?.getTime()
        ) {
            firstInstanceData.lastOrderDate = null
        }
        if (
            !currentData ||
            currentData.orderDate?.getTime() !== firstInstanceData.orderDate?.getTime()
        ) {
            firstInstanceData.orderDate = null
        }
        if (
            !currentData ||
            currentData.deliveryDate?.getTime() !== firstInstanceData.deliveryDate?.getTime()
        ) {
            firstInstanceData.deliveryDate = null
        }
        if (!currentData || !currentData.additionalFields?.length) {
            firstInstanceData.additionalFields = []
        }
    }

    const getCommonDatesForClusters = (stores: ActivityStore[]) => {
        const firstStore = datesMap[stores[0].storeId]
        const firstStoreDatesObject = {
            firstOrderDate: firstStore ? firstStore.firstOrderDate : null,
            lastOrderDate: firstStore ? firstStore.lastOrderDate : null,
            orderDate: firstStore ? firstStore.orderDate : null,
            deliveryDate: firstStore ? firstStore.deliveryDate : null,
            additionalFields: firstStore?.additionalFields ? firstStore?.additionalFields : []
        }
        stores.forEach((store, index) => {
            if (index !== 0) {
                const storeData = datesMap[store.storeId]
                nullDateIfNotSame(storeData, firstStoreDatesObject)
            }
        })

        return {
            firstOrderDate: firstStoreDatesObject.firstOrderDate,
            lastOrderDate: firstStoreDatesObject.lastOrderDate,
            orderDate: firstStoreDatesObject.orderDate,
            deliveryDate: firstStoreDatesObject.deliveryDate,
            additionalFields: getFilledAdditionalFields(firstStoreDatesObject)
        }
    }

    const getCommonDatesForRegions = (clusters: ActivityCluster[]) => {
        const firstClusterDatesObject = getCommonDatesForClusters(clusters[0].stores)
        clusters.forEach((cluster, index) => {
            if (index !== 0) {
                const clusterData = getCommonDatesForClusters(cluster.stores)
                nullDateIfNotSame(clusterData, firstClusterDatesObject)
            }
        })

        return {
            firstOrderDate: firstClusterDatesObject.firstOrderDate,
            lastOrderDate: firstClusterDatesObject.lastOrderDate,
            orderDate: firstClusterDatesObject.orderDate,
            deliveryDate: firstClusterDatesObject.deliveryDate,
            additionalFields: getFilledAdditionalFields(firstClusterDatesObject)
        }
    }

    const getCommonDatesForAllObjects = (regions: ActivityRegion[]) => {
        const firstRegionDatesObject = getCommonDatesForRegions(regions[0].clusters)
        regions.forEach((region, index) => {
            if (index !== 0) {
                const regionData = getCommonDatesForRegions(region.clusters)
                nullDateIfNotSame(regionData, firstRegionDatesObject)
            }
        })

        return {
            firstOrderDate: firstRegionDatesObject.firstOrderDate,
            lastOrderDate: firstRegionDatesObject.lastOrderDate,
            orderDate: firstRegionDatesObject.orderDate,
            deliveryDate: firstRegionDatesObject.deliveryDate,
            additionalFields: getFilledAdditionalFields(firstRegionDatesObject)
        }
    }

    const setLocationCalendarsFlatArray = (data: ActivityRegion[]) => {
        const array: LocationCalendars[] = []
        const allObjectsCommonDates = getCommonDatesForAllObjects(data)
        const allObjects = {
            ...locationCalendars[0],
            ...allObjectsCommonDates
        }

        data.forEach((region) => {
            const regionCommonDatesObject: LocationDatesSimple = getCommonDatesForRegions(
                region.clusters
            )
            array.push({
                parentId: -1,
                id: '',
                deliveryType: region.deliveryType,
                storedeliveryUnavailableWeekDays: region.storedeliveryUnavailableWeekDays,
                storeOrderDays: region.storeOrderDays,
                locationId: region.regionId,
                locationType: LocationType.Region,
                isNeedToErase: false,
                lastOrderDate: regionCommonDatesObject.lastOrderDate,
                firstOrderDate: regionCommonDatesObject.firstOrderDate,
                orderDate: regionCommonDatesObject.orderDate,
                deliveryDate: regionCommonDatesObject.deliveryDate,
                additionalFields: regionCommonDatesObject.additionalFields
                    ? regionCommonDatesObject.additionalFields
                    : []
            })

            region.clusters.map((cluster) => {
                const clusterCommonDatesObject = getCommonDatesForClusters(cluster.stores)
                array.push({
                    parentId: region.regionId,
                    id: '',
                    deliveryType: cluster.deliveryType,
                    storedeliveryUnavailableWeekDays: cluster.storedeliveryUnavailableWeekDays,
                    storeOrderDays: cluster.storeOrderDays,
                    locationId: cluster.clusterId,
                    locationType: LocationType.Cluster,
                    isNeedToErase: false,
                    lastOrderDate: clusterCommonDatesObject.lastOrderDate,
                    firstOrderDate: clusterCommonDatesObject.firstOrderDate,
                    orderDate: clusterCommonDatesObject.orderDate,
                    deliveryDate: clusterCommonDatesObject.deliveryDate,
                    additionalFields: clusterCommonDatesObject.additionalFields
                })

                cluster.stores.map((store) => {
                    const storeDatesObject = datesMap[store.storeId]
                    const additionalFields = (storeDatesObject?.additionalFields || []).map(
                        (el) => ({
                            ...el,
                            deliveryDate: el.deliveryDate,
                            orderDate: el.orderDate
                        })
                    )
                    const datesObject = { ...storeDatesObject, additionalFields }
                    array.push({
                        parentId: cluster.clusterId,
                        id: datesMap[store.storeId]?.id || '',
                        deliveryType: store.deliveryType,
                        storedeliveryUnavailableWeekDays: store.storedeliveryUnavailableWeekDays,
                        storeOrderDays: store.storeOrderDays,
                        locationId: store.storeId,
                        locationType: LocationType.Store,
                        isNeedToErase: false,
                        lastOrderDate: datesMap[store.storeId]?.lastOrderDate,
                        firstOrderDate: datesMap[store.storeId]?.firstOrderDate,
                        orderDate: datesMap[store.storeId]?.orderDate,
                        deliveryDate: datesMap[store.storeId]?.deliveryDate,
                        additionalFields: getFilledAdditionalFields(datesObject)
                    })
                })
            })
        })
        setLocationCalendars([allObjects, ...array])
    }

    const handleClickObjectTitle = (id: number, property: LocationType) => {
        if (property === LocationType.Store) return

        const copyArray = cloneDeep(regionData)
        let index = -1
        if (property === LocationType.Region) {
            index = copyArray.findIndex((region) => region.regionId === id)
            if (index !== -1) {
                copyArray[index].isActive = !copyArray[index].isActive
            }
        }
        if (property === LocationType.Cluster) {
            const regionIndex = copyArray.findIndex((region) => {
                index = region.clusters.findIndex((cluster) => cluster.clusterId === id)
                return index !== -1
            })
            if (regionIndex !== -1 && index !== -1) {
                copyArray[regionIndex].clusters[index].isActive =
                    !copyArray[regionIndex].clusters[index].isActive
            }
        }
        setRegionData(copyArray)
    }

    /**
     * Очищает календарные данные для всей строки указанного объекта (регион/город/магазин).
     *
     * @param {number | string} id - Идентификатор указанного объекта (regionId/clusterId/storeId)
     * @param {LocationType} property - Тип указанного объекта (All/Region/Cluster/Store)
     * */
    const clearCalendarRow = (id: number | string, property: LocationType) => {
        const index = locationCalendars.findIndex((item) => item.locationId === id)
        const regionDataArray = getRegionDataArray(regionData)

        // регион
        if (property === LocationType.Region) {
            const filteredRegionDataArray = regionDataArray.find(
                (regionData) => regionData.regionId === id
            )
            const flattenArray = getFlattenArray(filteredRegionDataArray as FlattenedData, property)
            const copyArray = cloneDeep(locationCalendars)
            copyArray.forEach((item) => {
                const flattenId = flattenArray.findIndex(
                    (flatten) =>
                        flatten.id === item.locationId && flatten.locationType === item.locationType
                )
                if (flattenId !== -1) {
                    item.lastOrderDate = null
                    item.firstOrderDate = null
                    item.deliveryDate = null
                    item.orderDate = null
                    item.isNeedToErase = true
                    if ('additionalFields' in item) {
                        delete (item as { additionalFields?: unknown }).additionalFields
                    }
                    item.additionalFields = []
                }
                return item
            })
            setLocationCalendars(copyArray)
        }

        // город
        if (property === LocationType.Cluster) {
            const getClusterData = (id: number | string) => {
                return regionDataArray.find((regionData) =>
                    regionData.clusters.some((cluster) => cluster.clusterId === id)
                )
            }

            const clusterData = getClusterData(id)
            const filteredClusterData = {
                ...clusterData,
                clusters: clusterData?.clusters.filter((cluster) => cluster.clusterId === id)
            }

            const flattenArray = getFlattenArray(filteredClusterData as FlattenedData, property)
            const copyArray = [...locationCalendars]
            const filteredCopyArray = copyArray.map((item) => {
                const flattenId = flattenArray.findIndex(
                    (flatten) =>
                        flatten.id === item.locationId && flatten.locationType === item.locationType
                )

                if (flattenId !== -1) {
                    item.deliveryDate = null
                    item.orderDate = null
                    item.lastOrderDate = null
                    item.firstOrderDate = null
                    if ('additionalFields' in item) {
                        delete (item as { additionalFields?: unknown }).additionalFields
                    }
                    item.additionalFields = []
                    return item
                }
                return item
            })
            setLocationCalendars(filteredCopyArray)
        }

        // магазин
        if (property === LocationType.Store) {
            if (index !== -1) {
                const arrayCopy = [...locationCalendars]
                arrayCopy[index].firstOrderDate = null
                arrayCopy[index].lastOrderDate = null
                arrayCopy[index].orderDate = null
                arrayCopy[index].deliveryDate = null
                arrayCopy[index].isNeedToErase = true
                if (arrayCopy[index] && 'additionalFields' in arrayCopy[index]) {
                    delete (arrayCopy[index] as { additionalFields?: unknown }).additionalFields
                }
                arrayCopy[index].additionalFields = []
                setLocationCalendars(arrayCopy)
            }
        }
    }

    const getUnavailableDays = (
        property: LocationType,
        id: number
    ): {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
        firstOrderDate: string
        lastOrderDate: string
        deliveryType: string
    } => {
        let unavailableDaysArray: UnavailableDay[] = []
        let orderDaysArray: OrderDay[] = []
        let firstOrderDate = ''
        let lastOrderDate = ''
        let deliveryType = ''
        if (property === LocationType.Region) {
            const index = regionData.findIndex((it) => it.regionId === id)
            if (index !== -1) {
                const storeUnavailableDaysArray =
                    regionData[index].storedeliveryUnavailableWeekDays || []
                const storeOrderDaysArray = regionData[index].storeOrderDays || []
                unavailableDaysArray = [...unavailableDaysArray, ...storeUnavailableDaysArray]
                orderDaysArray = [...orderDaysArray, ...storeOrderDaysArray]
                firstOrderDate = regionData[index].firstOrderDateRegion || ''
                lastOrderDate = regionData[index].lastOrderDateRegion || ''
                deliveryType = regionData[index].deliveryType || ''
            }
        }
        if (property === LocationType.Cluster) {
            const index = regionData.findIndex((it) => it.regionId === id)
            if (index !== -1) {
                deliveryType = regionData[index].deliveryType || ''
            }
            regionData.forEach((region) => {
                const index = region.clusters.findIndex((it) => it.clusterId === id)
                if (index !== -1) {
                    const storeUnavailableDaysArray =
                        region.clusters[index].storedeliveryUnavailableWeekDays || []
                    const storeOrderDaysArray = region.clusters[index].storeOrderDays || []
                    unavailableDaysArray = [...unavailableDaysArray, ...storeUnavailableDaysArray]
                    orderDaysArray = [...orderDaysArray, ...storeOrderDaysArray]
                    firstOrderDate = region.clusters[index].firstOrderDateCluster || ''
                    lastOrderDate = region.clusters[index].lastOrderDateCluster || ''
                    deliveryType = region.clusters[index].deliveryType || ''
                }
            })
        }
        if (property === LocationType.Store) {
            regionData.forEach((region) => {
                region.clusters.forEach((cluster) => {
                    const index = cluster.stores.findIndex((it) => it.storeId === id)
                    if (index !== -1) {
                        const storeUnavailableDaysArray =
                            cluster.stores[index].storedeliveryUnavailableWeekDays || []
                        const storeOrderDaysArray = cluster.stores[index].storeOrderDays || []
                        unavailableDaysArray = [
                            ...unavailableDaysArray,
                            ...storeUnavailableDaysArray
                        ]
                        orderDaysArray = [...orderDaysArray, ...storeOrderDaysArray]
                        firstOrderDate = cluster.stores[index].firstOrderDateStore || ''
                        lastOrderDate = cluster.stores[index].lastOrderDateStore || ''
                        deliveryType = cluster.stores[index].deliveryType || ''
                    }
                })
            })
        }
        return {
            unavailableDays: unavailableDaysArray.filter(
                (it1, index) =>
                    unavailableDaysArray.findIndex((it2) => it1.weekDay === it2.weekDay) === index
            ),
            orderDays: orderDaysArray.filter(
                (it1, index) =>
                    orderDaysArray.findIndex((it2) => it1.orderDay === it2.orderDay) === index
            ),
            firstOrderDate,
            lastOrderDate,
            deliveryType
        }
    }

    /**
     * Обрабатывает изменение даты в календаре для указанного объекта (все объекты/регион/город/магазин).
     * Обновляет соответствующее свойство даты в массиве locationCalendars.
     *
     * @param {Moment | null} currDate - Новая выбранная дата или null для очистки
     * @param {number} locationId - Идентификатор указанного объекта (regionId/clusterId/storeId)
     * @param {keyof LocationDates} property - Название типа ячейки
     * @param {LocationType} locationType - Тип указанного объекта (All/Region/Cluster/Store)
     * @param {string} [additionalId] - Идентификатор дополнительных полей календаря
     */
    const handleCalendarChange = useCallback(
        (
            currDate: Moment | null,
            locationId: number,
            property: keyof LocationDates,
            locationType: LocationType,
            additionalId?: string
        ) => {
            const date = currDate?.startOf('day')

            const index = locationCalendars.findIndex(
                (item) => item.locationId === locationId && item.locationType === locationType
            )

            if (index === -1 || property === 'additionalFields' || property === 'id') return

            let copyArray = cloneDeep(locationCalendars)

            /**
             * заполнение поля конкретного магазина (Кнопка Все магазины) additionalFields
             * */
            if (additionalId && (property === 'orderDate' || property === 'deliveryDate')) {
                const additionalIndex = copyArray[index].additionalFields.findIndex(
                    (item) => item.index === additionalId
                )

                if (additionalIndex !== -1) {
                    copyArray[index].additionalFields[additionalIndex][property] = date
                        ? getDateWithOffset(date.toDate()).toDate()
                        : null

                    const children = copyArray.reduce(
                        (
                            prev: {
                                id: string
                                index: string
                                orderDate: Date | null
                                deliveryDate: Date | null
                            }[],
                            curr: LocationCalendars
                        ) => {
                            if (curr.parentId === copyArray[index]?.parentId) {
                                return [
                                    ...prev,
                                    ...curr.additionalFields.filter(
                                        (el) =>
                                            el.index ===
                                            copyArray[index].additionalFields[additionalIndex].index
                                    )
                                ]
                            }
                            return prev
                        },
                        []
                    )

                    const filled = children.filter(
                        (el) =>
                            cloneDeep(el[property])?.setHours(0, 0, 0, 0) ===
                            cloneDeep(date)?.toDate().setHours(0, 0, 0, 0)
                    )

                    if (filled.length === children.length) {
                        const parentId = copyArray[index]?.parentId
                        const parentIndex = copyArray.findIndex(
                            (el) =>
                                el.locationId === parentId &&
                                el.locationType === childToParentTypeConfig[locationType]
                        )
                        if (parentIndex !== -1 && copyArray[parentIndex].additionalFields.length) {
                            copyArray[parentIndex].additionalFields[additionalIndex][property] =
                                date ? getDateWithOffset(date.toDate()).toDate() : null
                        }
                    } else {
                        const parentId = copyArray[index]?.parentId
                        const parentIndex = copyArray.findIndex(
                            (el) =>
                                el.locationId === parentId &&
                                el.locationType === childToParentTypeConfig[locationType]
                        )
                        if (parentIndex !== -1 && copyArray[parentIndex].additionalFields.length) {
                            copyArray[parentIndex].additionalFields[additionalIndex][property] =
                                null
                        }
                    }

                    /**
                     * когда удаляю дату
                     * */
                    if (
                        !date &&
                        locationId !== -1 &&
                        (property === 'deliveryDate' || property === 'orderDate')
                    ) {
                        copyArray[index].additionalFields[additionalIndex].orderDate = null

                        copyArray[index].additionalFields.forEach((_el, ind) => {
                            if (ind > additionalIndex) {
                                if (property === 'deliveryDate') {
                                    copyArray[index].additionalFields[ind].deliveryDate = null
                                }
                                copyArray[index].additionalFields[ind].orderDate = null
                            }
                        })

                        // Удаляет дополнительные поля, у которых не заполнены deliveryDate и orderDate
                        copyArray[index].additionalFields = deleteEmptyAdditionalFields(
                            copyArray[index].additionalFields
                        )
                    }
                }

                /**
                 * заполнение поля конкретного магазина (Кнопка Все магазины) основная строка
                 * */
            } else {
                /**
                 * когда ставлю дату
                 * */
                if (date) {
                    // безопасно получаем lastOrderDate
                    const rawLastOrderDate = copyArray[index]?.lastOrderDate
                    const validLastOrderDate =
                        rawLastOrderDate && moment(rawLastOrderDate).isValid()
                            ? moment(rawLastOrderDate)
                            : undefined

                    const locationId = copyArray[index].locationId

                    const { filteredApprovedDates } = getFilteredApprovedDates(
                        approvedData,
                        worksheetData,
                        locationId,
                        calendarId
                    )

                    const { disableFilteredDates } = getDisableFilteredDates(filteredApprovedDates)

                    const { isApprovedDates } = isApprovedFilteredDates(
                        disableFilteredDates,
                        date as Moment,
                        validLastOrderDate
                    )

                    if (date && !isDisableApprovedDates && isApprovedDates) {
                        setIsDisableApprovedDates(true)
                    }

                    const currDate = getDateWithOffset(date.toDate())

                    copyArray[index][property] = isApprovedDates ? null : currDate.toDate()

                    // если на основном поле deliveryDate больше orderDate, то очищаем все поля, которые идут после этого deliveryDate
                    if (property === 'orderDate' || property === 'deliveryDate') {
                        datesConfig[property].forEach((el) => {
                            if (el !== 'additionalFields' && el !== 'id') {
                                copyArray.forEach((item) => {
                                    const deliveryDate = item?.deliveryDate
                                    const orderDate = item?.orderDate
                                    const isDeliveryAfterOrder = moment(deliveryDate).isAfter(
                                        moment(orderDate),
                                        'day'
                                    )

                                    if (deliveryDate && orderDate && isDeliveryAfterOrder) {
                                        item.orderDate = null
                                        item.additionalFields = item.additionalFields.map(
                                            (field) => ({
                                                ...field,
                                                deliveryDate: null,
                                                orderDate: null
                                            })
                                        )
                                    }
                                })
                            }
                        })
                    }
                }

                /**
                 * когда удаляю дату
                 * */
                if (!date) {
                    datesConfig[property].map((el) => {
                        if (el !== 'additionalFields' && el !== 'id') {
                            copyArray[index][el] = null
                            copyArray[index].additionalFields = []
                        }
                    })

                    copyArray[index].additionalFields.map((_el, ind) => {
                        if (property === 'deliveryDate') {
                            copyArray[index].additionalFields[ind].deliveryDate = null
                        }
                        copyArray[index].additionalFields[ind].orderDate = null
                    })

                    // Удаляет дополнительные поля, у которых не заполнены deliveryDate и orderDate
                    copyArray[index].additionalFields = deleteEmptyAdditionalFields(
                        copyArray[index].additionalFields
                    )
                }
            }

            /**
             * заполнение поля Все объекты (Кнопка Все магазины)
             * */
            if (locationId === -1) {
                /**
                 * заполнение поля Все объекты (Кнопка Все магазины) additionalFields
                 * */
                if (additionalId && (property === 'orderDate' || property === 'deliveryDate')) {
                    const additionalIndex = copyArray[index].additionalFields.findIndex(
                        (item) => item.index === additionalId
                    )

                    if (additionalIndex !== -1) {
                        const id = copyArray[additionalIndex].locationId

                        const { deliveryType, ...unavailableDays } = getUnavailableDays(
                            locationType,
                            id
                        )

                        copyArray.forEach((item) => {
                            if (item.deliveryDate !== null && item.orderDate !== null) {
                                if (item.additionalFields.length <= 2) {
                                    item.additionalFields[additionalIndex][property] = date
                                        ? getDateValue({
                                              date: date.toDate(),
                                              disableDates: {
                                                  unavailableDays:
                                                      item.storedeliveryUnavailableWeekDays || [],
                                                  orderDays: item.storeOrderDays || []
                                              },
                                              copyArrayCalendar: item,
                                              approvedData: approvedData,
                                              worksheetData: worksheetData,
                                              locationId: item.locationId,
                                              property: property,
                                              unavailableDays: unavailableDays,
                                              deliveryType: deliveryType,
                                              additionalId: additionalId,
                                              calendarId: calendarId
                                          })
                                        : null
                                }
                            } else return null

                            // проверка на дублирующиеся даты на уровне всех объектов
                            const allObjectsAdditionalFields: PropertyAdditional[] =
                                copyArray[additionalIndex].additionalFields

                            if (property === 'deliveryDate' || property === 'orderDate') {
                                copyArray[additionalIndex].additionalFields =
                                    getUniqAdditionalFields(property, allObjectsAdditionalFields)
                            }
                        })

                        /**
                         * когда удаляю дату
                         * */
                        if (!date) {
                            copyArray.forEach((item) => {
                                // Находим индекс дополнительного поля по additionalId для текущего item
                                const fieldIndex = item.additionalFields.findIndex(
                                    (field) => field.index === additionalId
                                )

                                if (fieldIndex !== -1) {
                                    item.additionalFields[fieldIndex].orderDate = null

                                    // Очищаем все последующие поля
                                    item.additionalFields.forEach((_el, ind) => {
                                        if (ind > fieldIndex) {
                                            if (property === 'deliveryDate') {
                                                item.additionalFields[ind].deliveryDate = null
                                            }
                                            item.additionalFields[ind].orderDate = null
                                        }
                                    })

                                    // Удаляет дополнительные поля, у которых не заполнены deliveryDate и orderDate
                                    item.additionalFields = deleteEmptyAdditionalFields(
                                        item.additionalFields || []
                                    )
                                }
                            })
                        }
                    }

                    /**
                     * заполнение поля Все объекты (Кнопка Все магазины) основная строка
                     * */
                } else {
                    copyArray.forEach((item) => {
                        if (!date) return (item[property] = null)

                        const disableDates = {
                            unavailableDays: item.storedeliveryUnavailableWeekDays || [],
                            orderDays: item.storeOrderDays || []
                        }

                        let isDisabled = isCurrentDateUnavailable(
                            date,
                            disableDatesConfig[property],
                            disableDates
                        )

                        const prevDays: Array<WeekDay> = (disableDates?.orderDays || []).map(
                            (i) => prevDaysMap[i.orderDay]
                        )

                        const onlyRmsOrderDate = (currentDate: Moment) =>
                            disableDates
                                ? prevDays.includes(
                                      currentDate.locale('en').format(DAY_STRING_FORMAT) as WeekDay
                                  )
                                : false

                        // безопасно получаем lastOrderDate
                        const rawLastOrderDate = item?.lastOrderDate
                        const validLastOrderDate =
                            rawLastOrderDate && moment(rawLastOrderDate).isValid()
                                ? moment(rawLastOrderDate)
                                : undefined

                        const { filteredApprovedDates } = getFilteredApprovedDates(
                            approvedData,
                            worksheetData,
                            item.locationId,
                            calendarId
                        )

                        const { disableFilteredDates } =
                            getDisableFilteredDates(filteredApprovedDates)

                        const { isApprovedDates } = isApprovedFilteredDates(
                            disableFilteredDates,
                            date,
                            validLastOrderDate
                        )

                        if (date && !isDisableApprovedDates && isApprovedDates) {
                            setIsDisableApprovedDates(true)
                        }

                        isDisabled = isDisabled || isApprovedDates

                        if (property === 'lastOrderDate') {
                            isDisabled = isDisabled || onlyRmsOrderDate(date)
                        }

                        if (property === 'firstOrderDate') {
                            isDisabled = isDisabled || onlyRmsOrderDate(date) || !item.lastOrderDate
                        }

                        if (property === 'deliveryDate') {
                            isDisabled =
                                isDisabled ||
                                (!!item.lastOrderDate &&
                                    date.diff(item.lastOrderDate, 'day') === 0) ||
                                !item.firstOrderDate
                        }

                        if (property === 'orderDate') {
                            isDisabled = !item.deliveryDate
                        }

                        const currDate = getDateWithOffset(date.toDate())
                        item[property] = isDisabled ? null : currDate.toDate()

                        // если на всех объектах deliveryDate больше orderDate, то очищаем все поля, которые идут после этого deliveryDate
                        const deliveryDate = item?.deliveryDate
                        const orderDate = item?.orderDate
                        const isDeliveryAfterOrder = moment(deliveryDate).isAfter(
                            moment(orderDate),
                            'day'
                        )

                        if (deliveryDate && orderDate && isDeliveryAfterOrder) {
                            item.orderDate = null
                            item.deliveryDate = null
                            item.additionalFields = item.additionalFields.map((field) => ({
                                ...field,
                                deliveryDate: null,
                                orderDate: null
                            }))
                        }
                    })

                    /**
                     * когда удаляю дату
                     * */
                    if (!date) {
                        datesConfig[property].forEach((el) => {
                            if (el !== 'additionalFields' && el !== 'id') {
                                copyArray.forEach((item) => {
                                    item[el] = null
                                    item.additionalFields = []
                                })
                            }
                        })
                    }
                }

                /**
                 * заполнение поля Все регионы (Кнопка Все регионы)
                 * */
            } else {
                /**
                 * заполнение региона
                 * */
                if (locationType === LocationType.Region) {
                    const regionIndex = regionData.findIndex((item) => item.regionId === locationId)

                    if (regionIndex !== -1) {
                        /**
                         * города
                         * */
                        const clusters = regionData[regionIndex].clusters
                        clusters.forEach((cluster) => {
                            const clusterEachIndex = locationCalendars.findIndex(
                                (item) =>
                                    item.locationId === cluster.clusterId &&
                                    item.locationType === LocationType.Cluster
                            )

                            const id = copyArray[clusterEachIndex].locationId
                            const { deliveryType, ...unavailableDays } = getUnavailableDays(
                                locationType,
                                id
                            )

                            if (clusterEachIndex !== -1) {
                                /**
                                 * заполнение поля конкретного города (Кнопка Все регионы) additionalFields
                                 * */
                                if (
                                    additionalId &&
                                    (property === 'orderDate' || property === 'deliveryDate')
                                ) {
                                    const additionalIndex = copyArray[
                                        clusterEachIndex
                                    ].additionalFields.findIndex(
                                        (item) => item.index === additionalId
                                    )

                                    if (additionalIndex !== -1) {
                                        copyArray[clusterEachIndex].additionalFields[
                                            additionalIndex
                                        ][property] = date
                                            ? getDateValue({
                                                  date: date.toDate(),
                                                  disableDates: {
                                                      unavailableDays:
                                                          cluster.storedeliveryUnavailableWeekDays ||
                                                          [],
                                                      orderDays: cluster.storeOrderDays || []
                                                  },
                                                  copyArrayCalendar: copyArray[clusterEachIndex],
                                                  approvedData: approvedData,
                                                  worksheetData: worksheetData,
                                                  locationId: locationId,
                                                  property: property,
                                                  unavailableDays: unavailableDays,
                                                  deliveryType: deliveryType,
                                                  additionalId: additionalId,
                                                  calendarId: calendarId
                                              })
                                            : null

                                        /**
                                         * когда удаляю дату
                                         * */
                                        if (!date) {
                                            copyArray[clusterEachIndex].additionalFields[
                                                additionalIndex
                                            ].orderDate = null

                                            copyArray[clusterEachIndex].additionalFields.forEach(
                                                (_el, ind) => {
                                                    if (ind > additionalIndex) {
                                                        if (property === 'deliveryDate') {
                                                            copyArray[
                                                                clusterEachIndex
                                                            ].additionalFields[ind].deliveryDate =
                                                                null
                                                        }
                                                        copyArray[
                                                            clusterEachIndex
                                                        ].additionalFields[ind].orderDate = null
                                                    }
                                                }
                                            )

                                            // Удаляет дополнительные поля, у которых не заполнены deliveryDate и orderDate
                                            copyArray[clusterEachIndex].additionalFields =
                                                deleteEmptyAdditionalFields(
                                                    copyArray[clusterEachIndex].additionalFields
                                                )
                                        }
                                    }

                                    /**
                                     * заполнение поля конкретного города (Кнопка Все регионы) основная строка
                                     * */
                                } else {
                                    // безопасно получаем lastOrderDate
                                    const rawLastOrderDate =
                                        copyArray[clusterEachIndex]?.lastOrderDate
                                    const validLastOrderDate =
                                        rawLastOrderDate && moment(rawLastOrderDate).isValid()
                                            ? moment(rawLastOrderDate)
                                            : undefined

                                    copyArray[clusterEachIndex][property] = date
                                        ? getDateValue({
                                              date: date.toDate(),
                                              disableDates: {
                                                  unavailableDays:
                                                      cluster.storedeliveryUnavailableWeekDays ||
                                                      [],
                                                  orderDays: cluster.storeOrderDays || []
                                              },
                                              copyArrayCalendar: copyArray[clusterEachIndex],
                                              approvedData: approvedData,
                                              worksheetData: worksheetData,
                                              locationId: locationId,
                                              property: property,
                                              unavailableDays: unavailableDays,
                                              deliveryType: deliveryType,
                                              calendarId: calendarId,
                                              lastOrderDate: validLastOrderDate
                                          })
                                        : null

                                    // если на всех городах в регионе deliveryDate больше orderDate, то очищаем все поля, которые идут после этого deliveryDate
                                    const deliveryDate = copyArray[clusterEachIndex]?.deliveryDate
                                    const orderDate = copyArray[clusterEachIndex]?.orderDate
                                    const isDeliveryAfterOrder = moment(deliveryDate).isAfter(
                                        moment(orderDate),
                                        'day'
                                    )

                                    if (deliveryDate && orderDate && isDeliveryAfterOrder) {
                                        copyArray[clusterEachIndex].orderDate = null
                                        copyArray[clusterEachIndex].deliveryDate = null
                                        copyArray[clusterEachIndex].additionalFields = copyArray[
                                            clusterEachIndex
                                        ].additionalFields.map((field) => ({
                                            ...field,
                                            deliveryDate: null,
                                            orderDate: null
                                        }))
                                    }

                                    /**
                                     * когда удаляю дату
                                     * */
                                    if (!date) {
                                        datesConfig[property].forEach((el) => {
                                            if (el !== 'additionalFields' && el !== 'id') {
                                                copyArray[clusterEachIndex][el] = null
                                                copyArray[clusterEachIndex].additionalFields = []
                                            }
                                        })
                                    }
                                }

                                /**
                                 * магазины
                                 * */
                                cluster.stores.forEach((store) => {
                                    const storeEachIndex = locationCalendars.findIndex(
                                        (item) =>
                                            item.locationId === store.storeId &&
                                            item.locationType === LocationType.Store
                                    )

                                    const id = copyArray[storeEachIndex].locationId
                                    const { deliveryType, ...unavailableDays } = getUnavailableDays(
                                        locationType,
                                        id
                                    )

                                    if (storeEachIndex !== -1) {
                                        /**
                                         * заполнение поля конкретного магазина (Кнопка Все регионы) additionalFields
                                         * */
                                        if (
                                            additionalId &&
                                            (property === 'orderDate' ||
                                                property === 'deliveryDate')
                                        ) {
                                            // additional fields когда ставлю дату
                                            const additionalIndex = copyArray[
                                                storeEachIndex
                                            ].additionalFields.findIndex(
                                                (item) => item.index === additionalId
                                            )

                                            if (additionalIndex !== -1) {
                                                if (
                                                    copyArray[storeEachIndex].additionalFields
                                                        .length <= 2
                                                ) {
                                                    copyArray[storeEachIndex].additionalFields[
                                                        additionalIndex
                                                    ][property] = date
                                                        ? getDateValue({
                                                              date: date.toDate(),
                                                              disableDates: {
                                                                  unavailableDays:
                                                                      store.storedeliveryUnavailableWeekDays ||
                                                                      [],
                                                                  orderDays:
                                                                      store.storeOrderDays || []
                                                              },
                                                              copyArrayCalendar:
                                                                  copyArray[storeEachIndex],
                                                              approvedData: approvedData,
                                                              worksheetData: worksheetData,
                                                              locationId: store.storeId,
                                                              property: property,
                                                              unavailableDays: unavailableDays,
                                                              deliveryType: deliveryType,
                                                              additionalId: additionalId,
                                                              calendarId: calendarId
                                                          })
                                                        : null
                                                }

                                                /**
                                                 * когда удаляю дату
                                                 * */
                                                if (!date) {
                                                    copyArray[storeEachIndex].additionalFields[
                                                        additionalIndex
                                                    ].orderDate = null

                                                    copyArray[
                                                        storeEachIndex
                                                    ].additionalFields.forEach((_el, ind) => {
                                                        if (ind > additionalIndex) {
                                                            if (property === 'deliveryDate') {
                                                                copyArray[
                                                                    storeEachIndex
                                                                ].additionalFields[
                                                                    ind
                                                                ].deliveryDate = null
                                                            }
                                                            copyArray[
                                                                storeEachIndex
                                                            ].additionalFields[ind].orderDate = null
                                                        }
                                                    })

                                                    // Удаляет дополнительные поля, у которых не заполнены deliveryDate и orderDate
                                                    copyArray[storeEachIndex].additionalFields =
                                                        deleteEmptyAdditionalFields(
                                                            copyArray[storeEachIndex]
                                                                .additionalFields
                                                        )
                                                }
                                            }

                                            /**
                                             * заполнение поля конкретного магазина (Кнопка Все регионы) основная строка
                                             * */
                                        } else {
                                            // not additional fields когда ставлю дату

                                            // безопасно получаем lastOrderDate
                                            const rawLastOrderDate =
                                                copyArray[storeEachIndex]?.lastOrderDate
                                            const validLastOrderDate =
                                                rawLastOrderDate &&
                                                moment(rawLastOrderDate).isValid()
                                                    ? moment(rawLastOrderDate)
                                                    : undefined

                                            const locationId = copyArray[storeEachIndex].locationId

                                            const { filteredApprovedDates } =
                                                getFilteredApprovedDates(
                                                    approvedData,
                                                    worksheetData,
                                                    locationId,
                                                    calendarId
                                                )

                                            const { disableFilteredDates } =
                                                getDisableFilteredDates(filteredApprovedDates)

                                            const { isApprovedDates } = isApprovedFilteredDates(
                                                disableFilteredDates,
                                                date as Moment,
                                                validLastOrderDate
                                            )

                                            if (
                                                date &&
                                                !isDisableApprovedDates &&
                                                isApprovedDates
                                            ) {
                                                setIsDisableApprovedDates(true)
                                            }

                                            copyArray[storeEachIndex][property] = date
                                                ? getDateValue({
                                                      date: date.toDate(),
                                                      disableDates: {
                                                          unavailableDays:
                                                              store.storedeliveryUnavailableWeekDays ||
                                                              [],
                                                          orderDays: store.storeOrderDays || []
                                                      },
                                                      copyArrayCalendar: copyArray[storeEachIndex],
                                                      approvedData: approvedData,
                                                      worksheetData: worksheetData,
                                                      locationId: store.storeId,
                                                      property: property,
                                                      unavailableDays: unavailableDays,
                                                      deliveryType: deliveryType,
                                                      calendarId: calendarId,
                                                      lastOrderDate: validLastOrderDate
                                                  })
                                                : null

                                            // если на всех магазинах в городе deliveryDate больше orderDate, то очищаем все поля, которые идут после этого deliveryDate
                                            const deliveryDate =
                                                copyArray[storeEachIndex]?.deliveryDate
                                            const orderDate = copyArray[storeEachIndex]?.orderDate
                                            const isDeliveryAfterOrder = moment(
                                                deliveryDate
                                            ).isAfter(moment(orderDate), 'day')

                                            if (deliveryDate && orderDate && isDeliveryAfterOrder) {
                                                copyArray[storeEachIndex].orderDate = null
                                                copyArray[storeEachIndex].deliveryDate = null
                                                copyArray[storeEachIndex].additionalFields =
                                                    copyArray[storeEachIndex].additionalFields.map(
                                                        (field) => ({
                                                            ...field,
                                                            deliveryDate: null,
                                                            orderDate: null
                                                        })
                                                    )
                                            }

                                            /**
                                             * когда удаляю дату
                                             * */
                                            if (!date) {
                                                datesConfig[property].forEach((el) => {
                                                    if (el !== 'additionalFields' && el !== 'id') {
                                                        copyArray[storeEachIndex][el] = null
                                                        copyArray[storeEachIndex].additionalFields =
                                                            []
                                                    }
                                                })
                                            }
                                        }
                                    }

                                    // проверка на дублирующиеся даты на уровне регионов
                                    const storeAdditionalFields: PropertyAdditional[] =
                                        copyArray[storeEachIndex].additionalFields

                                    if (property === 'deliveryDate' || property === 'orderDate') {
                                        copyArray[storeEachIndex].additionalFields =
                                            getUniqAdditionalFields(property, storeAdditionalFields)
                                    }
                                })
                            }
                        })
                    }

                    /**
                     * заполнение магазина
                     * */
                } else if (locationType === LocationType.Cluster) {
                    regionData.forEach((region) => {
                        const clusterIndex = region.clusters.findIndex(
                            (item) => item.clusterId === locationId
                        )

                        if (clusterIndex !== -1) {
                            /**
                             * магазины
                             * */
                            const stores = region.clusters[clusterIndex].stores
                            stores.forEach((store) => {
                                const storeEachIndex = locationCalendars.findIndex(
                                    (item) =>
                                        item.locationId === store.storeId &&
                                        item.locationType === LocationType.Store
                                )

                                const id = copyArray[storeEachIndex].locationId
                                const { deliveryType, ...unavailableDays } = getUnavailableDays(
                                    locationType,
                                    id
                                )

                                if (storeEachIndex !== -1) {
                                    /**
                                     * заполнение поля конкретного магазина (Кнопка Все регионы) additionalFields
                                     * */
                                    if (
                                        additionalId &&
                                        (property === 'orderDate' || property === 'deliveryDate')
                                    ) {
                                        // additional fields когда ставлю дату
                                        const additionalIndex = copyArray[
                                            storeEachIndex
                                        ].additionalFields.findIndex(
                                            (item) => item.index === additionalId
                                        )

                                        if (additionalIndex !== -1) {
                                            if (
                                                copyArray[storeEachIndex].additionalFields.length <=
                                                2
                                            ) {
                                                copyArray[storeEachIndex].additionalFields[
                                                    additionalIndex
                                                ][property] = date
                                                    ? getDateValue({
                                                          date: date.toDate(),
                                                          disableDates: {
                                                              unavailableDays:
                                                                  store.storedeliveryUnavailableWeekDays ||
                                                                  [],
                                                              orderDays: store.storeOrderDays || []
                                                          },
                                                          copyArrayCalendar:
                                                              copyArray[storeEachIndex],
                                                          approvedData: approvedData,
                                                          worksheetData: worksheetData,
                                                          locationId: store.storeId,
                                                          property: property,
                                                          unavailableDays: unavailableDays,
                                                          deliveryType: deliveryType,
                                                          additionalId: additionalId,
                                                          calendarId: calendarId
                                                      })
                                                    : null
                                            }

                                            /**
                                             * когда удаляю дату
                                             * */
                                            if (!date) {
                                                copyArray[storeEachIndex].additionalFields[
                                                    additionalIndex
                                                ].orderDate = null

                                                copyArray[storeEachIndex].additionalFields.forEach(
                                                    (_el, ind) => {
                                                        if (ind > additionalIndex) {
                                                            if (property === 'deliveryDate') {
                                                                copyArray[
                                                                    storeEachIndex
                                                                ].additionalFields[
                                                                    ind
                                                                ].deliveryDate = null
                                                            }
                                                            copyArray[
                                                                storeEachIndex
                                                            ].additionalFields[ind].orderDate = null
                                                        }
                                                    }
                                                )

                                                // Удаляет дополнительные поля, у которых не заполнены deliveryDate и orderDate
                                                copyArray[storeEachIndex].additionalFields =
                                                    deleteEmptyAdditionalFields(
                                                        copyArray[storeEachIndex].additionalFields
                                                    )
                                            }
                                        }

                                        /**
                                         * заполнение поля конкретного магазина (Кнопка Все регионы) основная строка
                                         * */
                                    } else {
                                        // not additional fields когда ставлю дату

                                        // безопасно получаем lastOrderDate
                                        const rawLastOrderDate =
                                            copyArray[storeEachIndex]?.lastOrderDate
                                        const validLastOrderDate =
                                            rawLastOrderDate && moment(rawLastOrderDate).isValid()
                                                ? moment(rawLastOrderDate)
                                                : undefined

                                        const locationId = copyArray[storeEachIndex].locationId

                                        const { filteredApprovedDates } = getFilteredApprovedDates(
                                            approvedData,
                                            worksheetData,
                                            locationId,
                                            calendarId
                                        )

                                        const { disableFilteredDates } =
                                            getDisableFilteredDates(filteredApprovedDates)

                                        const { isApprovedDates } = isApprovedFilteredDates(
                                            disableFilteredDates,
                                            date as Moment,
                                            validLastOrderDate
                                        )

                                        if (date && !isDisableApprovedDates && isApprovedDates) {
                                            setIsDisableApprovedDates(true)
                                        }

                                        copyArray[storeEachIndex][property] = date
                                            ? getDateValue({
                                                  date: date.toDate(),
                                                  disableDates: {
                                                      unavailableDays:
                                                          store.storedeliveryUnavailableWeekDays ||
                                                          [],
                                                      orderDays: store.storeOrderDays || []
                                                  },
                                                  copyArrayCalendar: copyArray[storeEachIndex],
                                                  approvedData: approvedData,
                                                  worksheetData: worksheetData,
                                                  locationId: store.storeId,
                                                  property: property,
                                                  unavailableDays: unavailableDays,
                                                  deliveryType: deliveryType,
                                                  calendarId: calendarId,
                                                  lastOrderDate: validLastOrderDate
                                              })
                                            : null

                                        // если на всех магазинах в городе deliveryDate больше orderDate, то очищаем все поля, которые идут после этого deliveryDate
                                        const deliveryDate = copyArray[storeEachIndex]?.deliveryDate
                                        const orderDate = copyArray[storeEachIndex]?.orderDate
                                        const isDeliveryAfterOrder = moment(deliveryDate).isAfter(
                                            moment(orderDate),
                                            'day'
                                        )

                                        if (deliveryDate && orderDate && isDeliveryAfterOrder) {
                                            copyArray[storeEachIndex].orderDate = null
                                            copyArray[storeEachIndex].deliveryDate = null
                                            copyArray[storeEachIndex].additionalFields = copyArray[
                                                storeEachIndex
                                            ].additionalFields.map((field) => ({
                                                ...field,
                                                deliveryDate: null,
                                                orderDate: null
                                            }))
                                        }

                                        /**
                                         * когда удаляю дату
                                         * */
                                        if (!date) {
                                            datesConfig[property].forEach((el) => {
                                                if (el !== 'additionalFields' && el !== 'id') {
                                                    copyArray[storeEachIndex][el] = null
                                                    copyArray[storeEachIndex].additionalFields = []
                                                }
                                            })
                                        }
                                    }
                                }

                                // проверка на дублирующиеся даты на уровне городов
                                const storeAdditionalFields: PropertyAdditional[] =
                                    copyArray[storeEachIndex].additionalFields

                                if (property === 'deliveryDate' || property === 'orderDate') {
                                    copyArray[storeEachIndex].additionalFields =
                                        getUniqAdditionalFields(property, storeAdditionalFields)
                                }
                            })
                        }
                    })
                }
            }

            /**
             * заполнение основных полей
             * */
            if (!additionalId) {
                const children = copyArray.filter(
                    (el) => el.parentId === copyArray[index]?.parentId
                )

                const filled = children.filter(
                    (el) =>
                        cloneDeep(el[property])?.setHours(0, 0, 0, 0) ===
                        cloneDeep(date)?.toDate().setHours(0, 0, 0, 0)
                )

                if (filled.length === children.length) {
                    const parentId = copyArray[index]?.parentId
                    const parentIndex = copyArray.findIndex(
                        (el) =>
                            el.locationId === parentId &&
                            el.locationType === childToParentTypeConfig[locationType]
                    )
                    if (parentIndex !== -1) {
                        copyArray[parentIndex][property] = date
                            ? getDateWithOffset(date.toDate()).toDate()
                            : null
                    }
                } else {
                    const parentId = copyArray[index]?.parentId
                    const parentIndex = copyArray.findIndex(
                        (el) =>
                            el.locationId === parentId &&
                            el.locationType === childToParentTypeConfig[locationType]
                    )
                    if (parentIndex !== -1) {
                        copyArray[0][property] = null
                        copyArray[parentIndex][property] = null
                        if (locationType === LocationType.Store) {
                            const secondParentId = copyArray[parentIndex]?.parentId
                            const secondParentIndex = copyArray.findIndex(
                                (el) =>
                                    el.locationId === secondParentId &&
                                    el.locationType === LocationType.Region
                            )
                            if (secondParentIndex !== -1) {
                                copyArray[secondParentIndex][property] = null
                            }
                        }
                    }
                }
            }

            copyArray = updateDatesRecursively(copyArray, index)
            setLocationCalendars(copyArray)
        },
        [locationCalendars]
    )

    const onAddAdditional = useCallback(
        (additionalId: string, property: LocationType, parentId: number) => {
            const index = locationCalendars.findIndex(
                (item) => item.locationId === parentId && item.locationType === property
            )
            if (index === -1) return
            const copyArray = cloneDeep(locationCalendars)
            if (parentId === -1) {
                copyArray.forEach((item) => {
                    if (item.deliveryDate !== null && item.orderDate !== null) {
                        item.additionalFields.push({
                            index: additionalId,
                            id: '',
                            orderDate: null,
                            deliveryDate: null
                        })
                    }
                })
            } else {
                if (copyArray[index].deliveryDate !== null && copyArray[index].orderDate !== null) {
                    copyArray[index].additionalFields.push({
                        index: additionalId,
                        id: '',
                        orderDate: null,
                        deliveryDate: null
                    })
                }
            }
            if (property === LocationType.Region) {
                const regionIndex = regionData.findIndex((item) => item.regionId === parentId)
                if (regionIndex !== -1) {
                    regionData[regionIndex].clusters.forEach((cluster) => {
                        const clusterEachIndex = locationCalendars.findIndex(
                            (item) =>
                                item.locationId === cluster.clusterId &&
                                item.locationType === LocationType.Cluster
                        )
                        if (clusterEachIndex !== -1) {
                            if (
                                copyArray[clusterEachIndex].deliveryDate !== null &&
                                copyArray[clusterEachIndex].orderDate !== null
                            ) {
                                copyArray[clusterEachIndex].additionalFields.push({
                                    index: additionalId,
                                    id: '',
                                    orderDate: null,
                                    deliveryDate: null
                                })
                            }

                            cluster.stores.forEach((store) => {
                                const storeEachIndex = locationCalendars.findIndex(
                                    (item) =>
                                        item.locationId === store.storeId &&
                                        item.locationType === LocationType.Store
                                )
                                if (storeEachIndex !== -1) {
                                    if (
                                        copyArray[storeEachIndex].deliveryDate !== null &&
                                        copyArray[storeEachIndex].orderDate !== null
                                    ) {
                                        copyArray[storeEachIndex].additionalFields.push({
                                            index: additionalId,
                                            id: '',
                                            orderDate: null,
                                            deliveryDate: null
                                        })
                                    }
                                }
                            })
                        }
                    })
                }
            } else if (property === LocationType.Cluster) {
                regionData.forEach((region) => {
                    const clusterIndex = region.clusters.findIndex(
                        (item) => item.clusterId === parentId
                    )
                    if (clusterIndex !== -1) {
                        region.clusters[clusterIndex].stores.forEach((store) => {
                            const storeEachIndex = locationCalendars.findIndex(
                                (item) =>
                                    item.locationId === store.storeId &&
                                    item.locationType === LocationType.Store
                            )
                            if (storeEachIndex !== -1) {
                                if (
                                    copyArray[storeEachIndex].deliveryDate !== null &&
                                    copyArray[storeEachIndex].orderDate !== null
                                ) {
                                    copyArray[storeEachIndex].additionalFields.push({
                                        index: additionalId,
                                        id: '',
                                        orderDate: null,
                                        deliveryDate: null
                                    })
                                }
                            }
                        })
                    }
                })
            }
            setLocationCalendars(copyArray)
        },
        [locationCalendars]
    )

    const onDeleteAdditional = useCallback(
        (additionalId: string, property: LocationType, parentId: number) => {
            const index = locationCalendars.findIndex(
                (item) => item.locationId === parentId && item.locationType === property
            )
            if (index === -1) return

            const copyArray = cloneDeep(locationCalendars)

            if (parentId === -1) {
                copyArray.map((item) => {
                    item.additionalFields = item.additionalFields.filter(
                        (item) => item.index !== additionalId
                    )
                })
            } else {
                copyArray[index].additionalFields = copyArray[index].additionalFields.filter(
                    (item) => item.index !== additionalId
                )
            }

            if (property === LocationType.Region) {
                const regionIndex = regionData.findIndex((item) => item.regionId === parentId)
                if (regionIndex !== -1) {
                    regionData[regionIndex].clusters.map((cluster) => {
                        const clusterEachIndex = locationCalendars.findIndex(
                            (item) =>
                                item.locationId === cluster.clusterId &&
                                item.locationType === LocationType.Cluster
                        )
                        if (clusterEachIndex !== -1) {
                            copyArray[clusterEachIndex].additionalFields = copyArray[
                                clusterEachIndex
                            ].additionalFields.filter((item) => item.index !== additionalId)
                            cluster.stores.map((store) => {
                                const storeEachIndex = locationCalendars.findIndex(
                                    (item) =>
                                        item.locationId === store.storeId &&
                                        item.locationType === LocationType.Store
                                )
                                if (storeEachIndex !== -1) {
                                    copyArray[storeEachIndex].additionalFields = copyArray[
                                        storeEachIndex
                                    ].additionalFields.filter((item) => item.index !== additionalId)
                                }
                            })
                        }
                    })
                }
            } else if (property === LocationType.Cluster) {
                regionData.forEach((region) => {
                    const clusterIndex = region.clusters.findIndex(
                        (item) => item.clusterId === parentId
                    )
                    if (clusterIndex !== -1) {
                        region.clusters[clusterIndex].stores.forEach((store) => {
                            const storeEachIndex = locationCalendars.findIndex(
                                (item) =>
                                    item.locationId === store.storeId &&
                                    item.locationType === LocationType.Store
                            )
                            if (storeEachIndex !== -1) {
                                copyArray[storeEachIndex].additionalFields = copyArray[
                                    storeEachIndex
                                ].additionalFields.filter((item) => item.index !== additionalId)
                            }
                        })
                    }
                })
            }
            setLocationCalendars(copyArray)
        },
        [locationCalendars]
    )

    const getContent = <T extends ActivityStore | ActivityCluster>(
        id: number,
        title: string,
        property: LocationType,
        needToErase: boolean,
        isAllShops: boolean,
        depth?: number,
        parentId?: number,
        isActive?: boolean,
        getChildrenContent?: (children: T[], parentId: number) => ReactNode,
        children?: T[]
    ) => {
        const calendarObject = locationCalendars.find(
            (it) => it.locationId === id && it.locationType === property
        )

        const defaultValues: DefaultDatesState = {
            firstOrder:
                calendarObject && calendarObject.firstOrderDate
                    ? moment(calendarObject.firstOrderDate)
                    : null,
            lastOrder:
                calendarObject && calendarObject.lastOrderDate
                    ? moment(calendarObject.lastOrderDate)
                    : null,
            order:
                calendarObject && calendarObject.orderDate
                    ? moment(calendarObject.orderDate)
                    : null,
            delivery:
                calendarObject && calendarObject.deliveryDate
                    ? moment(calendarObject.deliveryDate)
                    : null,
            additionalFields:
                calendarObject && calendarObject.additionalFields.length
                    ? calendarObject.additionalFields.map((item) => ({
                          ...item,
                          deliveryDate: item.deliveryDate ? moment(item.deliveryDate) : null,
                          orderDate: item.orderDate ? moment(item.orderDate) : null
                      }))
                    : []
        }

        let parentLocationType: LocationType

        switch (property) {
            case LocationType.Store:
                parentLocationType = LocationType.Cluster
                break
            case LocationType.Cluster:
                parentLocationType = LocationType.Region
                break
            case LocationType.Region:
            default:
                parentLocationType = LocationType.All
                break
        }

        const mainData = parentId
            ? locationCalendars.find(
                  (it) => it.locationId === parentId && it.locationType === parentLocationType
              )
            : locationCalendars[0]

        const { deliveryType, ...unavailableDays } = getUnavailableDays(property, id)

        return (
            <div className="object-children-group" key={id}>
                <div className="object-row">
                    <div className="object-title-container">
                        {!isInfoCalendar(type) && (
                            <Popover
                                content={<span>{getClearCalendarRowDescription(property)}</span>}
                                title="Очистить данные"
                                overlayClassName="calendars-title-popover"
                            >
                                <BiEraser
                                    className="clear-row"
                                    onClick={() => clearCalendarRow(id, property)}
                                />
                            </Popover>
                        )}
                        <RegularButton
                            variant="pseudo"
                            size="s"
                            style={
                                isInfoCalendar(type)
                                    ? depthStyle[depth ? 'depth-' + depth : 'depth-0']
                                    : {}
                            }
                            className={`object-title ${depth ? 'depth-' + depth : ''} ${
                                isActive ? 'isBtnActive' : ''
                            }`}
                            onClick={() => handleClickObjectTitle(id, property)}
                        >
                            {property === LocationType.Store ? `${title} ${id}` : title}
                        </RegularButton>
                    </div>
                    <CalendarsRow
                        calendarId={calendarId}
                        locationId={calendarObject?.locationId}
                        isInfo={isInfoCalendar(type)}
                        className="object-calendars-row"
                        isNeedToClear={isNeedToErase || needToErase}
                        disableDates={unavailableDays}
                        deliveryType={deliveryType}
                        onCalendarChange={(date, calendarProperty, additionalId) =>
                            handleCalendarChange(date, id, calendarProperty, property, additionalId)
                        }
                        onAddAdditional={(additionalId) =>
                            onAddAdditional(additionalId, property, id)
                        }
                        onDeleteAdditional={(additionalId) =>
                            onDeleteAdditional(additionalId, property, id)
                        }
                        mainData={mainData}
                        defaultValues={defaultValues}
                        isMayOrNewYearHoliday={isMayOrNewYearHoliday}
                        locationType={property}
                    />
                </div>
                {isActive &&
                    !isAllShops &&
                    getChildrenContent &&
                    children &&
                    getChildrenContent(children, id)}
            </div>
        )
    }

    /**
     * Открыть все регионы
     * */
    const getRegionsContent = () =>
        regionData.map((region) =>
            getContent<ActivityCluster>(
                region.regionId,
                region.regionName,
                LocationType.Region,
                locationCalendars.find((it) => it.locationId === region.regionId)?.isNeedToErase ??
                    false,
                false,
                undefined,
                undefined,
                region.isActive,
                getClusterContent,
                region.clusters
            )
        )

    const getClusterContent = (clusters: ActivityCluster[], parentId: number) =>
        clusters.map((cluster) =>
            getContent<ActivityStore>(
                cluster.clusterId,
                cluster.clusterName,
                LocationType.Cluster,
                locationCalendars.find((it) => it.locationId === cluster.clusterId)
                    ?.isNeedToErase ?? false,
                false,
                1,
                parentId,
                cluster.isActive,
                getStoreContent,
                cluster.stores
            )
        )

    const getStoreContent = (stores: ActivityStore[], parentId: number) =>
        stores.map((store) =>
            getContent(
                store.storeId,
                store.storeName,
                LocationType.Store,
                locationCalendars.find((it) => it.locationId === store.storeId)?.isNeedToErase ??
                    false,
                false,
                2,
                parentId
            )
        )

    /**
     * Открыть все магазины
     * */
    const getAllShops = () => {
        let shops: ActivityStore[] = []
        regionData.forEach((region) => {
            region.clusters.forEach((cluster) => {
                shops = [...shops, ...cluster.stores]
            })
        })
        return shops.map((shop) =>
            getContent(
                shop.storeId,
                shop.storeName,
                LocationType.Store,
                locationCalendars.find((it) => it.locationId === shop.storeId)?.isNeedToErase ??
                    false,
                true
            )
        )
    }

    return (
        <div className="calendar-page-content">
            <div className="fixed-part">
                <div className="row">
                    <RegularButton
                        variant="pseudo"
                        size="s"
                        iconLeft={<LeftCircleOutlined />}
                        className="back-btn"
                        onClick={() => history.goBack()}
                    >
                        Назад
                    </RegularButton>
                    <div className="headers">
                        <div className="headers-pair">
                            <div className="header">
                                <div className="title">Дата получения последнего заказа</div>
                                <div className="description">
                                    &#40;Укажите дату когда Вы хотите получить заказ, который Вы
                                    отгрузите до периода вашей недоступности и доставите без
                                    нарушения контрактного срока поставки&#41;
                                </div>
                            </div>
                            <div className="dice-stub" />
                            <div className="header">
                                <div className="title">Дата получения первого заказа</div>
                                <div className="description">
                                    &#40;Укажите дату, когда Вы хотите получить первый заказ после
                                    вашего периода недоступности&#41;
                                </div>
                            </div>
                        </div>
                        <div className="headers-pair">
                            <div className="header">
                                <div className="title">
                                    Дата получения заказа с поставкой в дни недоступности
                                </div>
                                <div className="description">
                                    &#40;Укажите день, когда Вы готовы получить заказ, который
                                    сможете доставить в период вашей недоступности. Если не готовы
                                    получить заказ, оставьте поле пустым&#41;
                                </div>
                            </div>
                            <div className="dice-stub" />
                            <div className="header">
                                <div className="title">Дата доставки в дни недоступности</div>
                                <div className="description">
                                    &#40;Укажите дату внутри периода недоступности, когда планируете
                                    доставить заказ. Если не будете осуществлять поставку в период
                                    недоступности, оставьте поле пустым&#41;
                                </div>
                            </div>
                        </div>
                        {!isInfoCalendar(type) && <div className="action-stub" />}
                    </div>
                </div>
                <div className="row">
                    <RegularButton
                        size="s"
                        variant="secondary"
                        className={`all-objects-btn ${
                            openBtnState === OpenBtnType.All ? 'active' : ''
                        }`}
                        onClick={() => setOpenBtnState(OpenBtnType.All)}
                    >
                        Все объекты
                    </RegularButton>
                    <CalendarsRow
                        calendarId={calendarId}
                        locationId={-1}
                        disableDates={{
                            ...orderDatesAll
                        }}
                        deliveryType={allDeliveryType}
                        isInfo={isInfoCalendar(type)}
                        isNeedToClear={isNeedToErase}
                        className="main-calendars"
                        defaultValues={{
                            firstOrder: locationCalendars[0].firstOrderDate
                                ? moment(locationCalendars[0].firstOrderDate)
                                : null,
                            lastOrder: locationCalendars[0].lastOrderDate
                                ? moment(locationCalendars[0].lastOrderDate)
                                : null,
                            order: locationCalendars[0].orderDate
                                ? moment(locationCalendars[0].orderDate)
                                : null,
                            delivery: locationCalendars[0].deliveryDate
                                ? moment(locationCalendars[0].deliveryDate)
                                : null,
                            additionalFields: locationCalendars[0].additionalFields.length
                                ? locationCalendars[0].additionalFields.map((item) => ({
                                      ...item,
                                      deliveryDate: item.deliveryDate
                                          ? moment(item.deliveryDate)
                                          : null,
                                      orderDate: item.orderDate ? moment(item.orderDate) : null
                                  }))
                                : []
                        }}
                        onCalendarChange={(date, property, additionalId) =>
                            handleCalendarChange(date, -1, property, LocationType.All, additionalId)
                        }
                        onAddAdditional={(additionalId) =>
                            onAddAdditional(additionalId, LocationType.All, -1)
                        }
                        onDeleteAdditional={(additionalId) =>
                            onDeleteAdditional(additionalId, LocationType.All, -1)
                        }
                        isMayOrNewYearHoliday={isMayOrNewYearHoliday}
                        isDisabled={regionData.length === 0}
                        locationType={LocationType.All}
                    />
                </div>
                <Spin
                    indicator={<LoadingOutlined spin />}
                    size="large"
                    style={{ color: '#fdc300' }}
                    spinning={regionData.length === 0}
                >
                    <div className="additionalFields-actions">
                        <RegularButton
                            size="s"
                            variant="secondary"
                            className={`additionalFields-btn ${
                                openBtnState === OpenBtnType.Districts ? 'active' : ''
                            }`}
                            onClick={() => setOpenBtnState(OpenBtnType.Districts)}
                        >
                            Открыть все регионы
                        </RegularButton>
                        <RegularButton
                            size="s"
                            variant="secondary"
                            className={`additionalFields-btn ${
                                openBtnState === OpenBtnType.Shops ? 'active' : ''
                            }`}
                            onClick={() => setOpenBtnState(OpenBtnType.Shops)}
                        >
                            Открыть все магазины
                        </RegularButton>
                    </div>
                </Spin>
            </div>
            <div className="instances-content">
                {openBtnState === OpenBtnType.Districts && getRegionsContent()}
                {openBtnState === OpenBtnType.Shops && getAllShops()}
            </div>
        </div>
    )
}
